// src/pages/workflow/WorkflowDetailPage.tsx
// Pipeline detail — the place a person operates one pipeline. Tabs group the
// tasks that happen together: watch runs, manage schedules, roll versions
// back, keep environments, test, and govern (settings). Every command here is
// wired to the workflow client; the surrounding grid/list context is never lost.

import { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useWorkflow, useWorkflowPipelinePolicy, useI18n, useAuth } from '@react/hooks';
import { authzApi, userprofileApi, workflowApi } from '@core/api';
import type { PipelineRunAsRequest } from '@core/types/workflow';
import type { Entity } from '@core/types/entity';
import { canDecideRunAsRequest, listAllRunAsRequests } from '@/features/workflow/runAsPermissions';
import { RunAsActorPicker } from '@/features/workflow/RunAsActorPicker';
import {
  Button,
  Badge,
  Input,
  Textarea,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Alert,
  ErrorAlert,
} from '@react/components';
import { Select } from '@/components/Select';
import { AnchoredMenu } from '@/components/AnchoredMenu';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { UserName } from '@/features/userprofile/UserName';
import { useTabId } from '@/components/tabs/TabContext';
import { entityStore } from '@/stores/EntityStore';
import { Modal } from '@/features/workflow/Modal';
import { FormDialog, TitleInput, AutoGrowTextarea, SegmentedControl } from '@/components/form';
import { KeyValueEditor } from '@/features/workflow/KeyValueEditor';
import {
  pipelineStatusMap,
  executionStatusMap,
  triggerTypeMap,
  triggerTypeOptions,
  errorHandlingOptions,
  environmentTypeOptions,
  scheduleTypeOptions,
  executionStatusFilterOptions,
  PIPELINE_TABS,
  getEntityId,
  toBadgeVariant,
  formatDateTime,
  formatRelativeTime,
  formatDuration,
  formatInterval,
  describeCron,
  previewIntervalNextRun,
  isExecutionActive,
  isExecutionRetryable,
  validateScheduleForm,
} from '@/features/workflow/logic';
import type { PipelineTab } from '@/features/workflow/logic';

// --- Inline SVG icons ---

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
  );
}

function EditIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function CopyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
    </svg>
  );
}

function MoreIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="1" /><circle cx="12" cy="5" r="1" /><circle cx="12" cy="19" r="1" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function DownloadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

// --- Local tab data uses loose server shapes ---
type Row = any;

function unwrapItems(res: any): Row[] {
  const items = res?.items?.items ?? res?.items;
  return Array.isArray(items) ? items : [];
}

export const WorkflowDetailPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useWorkflow();
  const auth = useAuth();
  const tabId = useTabId();
  const { t, locale } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const activeTab: PipelineTab = PIPELINE_TABS.some((tab) => tab.id === requestedTab)
    ? requestedTab as PipelineTab : 'overview';
  const [showActions, setShowActions] = useState(false);
  const actionsTriggerRef = useRef<HTMLButtonElement>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [item, setItem] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [access, setAccess] = useState({ view: false, owner: false, platformMember: false, platformOwner: false });
  const [accessLoading, setAccessLoading] = useState(true);

  const reload = useCallback(() => {
    if (id) {
      store.GetPipeline?.(id)
        .then((r: any) => { setItem(r?.pipeline ?? r); setLoadError(null); })
        .catch((error: unknown) => setLoadError(error))
        .finally(() => setLoading(false));
    }
  }, [id]);

  useEffect(() => {
    setItem(null);
    setLoading(true);
    reload();
  }, [id]);

  useEffect(() => {
    if (!item?.name || !id || getEntityId(item) !== id) return;
    entityStore.loadEntity(tabId, { urn: '', type: 'pipeline', id, title: item.name });
    return () => entityStore.clearContext(tabId);
  }, [tabId, id, item?.name]);

  useEffect(() => {
    const profileId = auth.activeProfileId;
    const platformId = item?.entity?.platform_id;
    if (!id || !profileId || !platformId) { setAccessLoading(false); return; }
    let cancelled = false;
    setAccessLoading(true);
    const check = (relation: string, object: string) => authzApi.Check({
      user: `profile:${profileId}`, relation, object, authorization_model_id: '',
    }).then((response) => response.allowed).catch(() => false);
    Promise.all([
      check('view', `pipeline:${id}`),
      check('owner', `pipeline:${id}`),
      check('member', `platform:${platformId}`),
      check('owner', `platform:${platformId}`),
    ]).then(([view, owner, platformMember, platformOwner]) => {
      if (!cancelled) setAccess({ view: owner || view || (platformMember && (item?.metadata?.visibility === 'platform' || item?.is_system)), owner, platformMember, platformOwner });
    }).finally(() => { if (!cancelled) setAccessLoading(false); });
    return () => { cancelled = true; };
  }, [id, item?.entity?.platform_id, auth.activeProfileId]);

  const visibleTabs = PIPELINE_TABS.filter((tab) => {
    if (tab.id === 'overview') return true;
    if (tab.id === 'settings') return access.owner;
    return access.view;
  });
  const shownTab = visibleTabs.some((tab) => tab.id === activeTab) ? activeTab : 'overview';

  const handleTabChange = (tab: PipelineTab) => {
    setSearchParams(tab === 'overview' ? {} : { tab });
  };

  const handleExecute = async () => {
    if (!id) return;
    try {
      const result = await store.ExecutePipeline?.(id, {});
      const execId = getEntityId(result?.execution || result);
      if (execId) navigate(`/workflows/executions/${execId}`);
    } catch {
      /* surfaced by store.error */
    }
  };

  const handlePublish = async () => { if (!id) return; await store.PublishPipeline?.(id, {}); reload(); };
  const handlePause = async () => { if (!id) return; await store.PausePipeline?.(id, {}); reload(); };
  const handleResume = async () => { if (!id) return; await store.ResumePipeline?.(id, {}); reload(); };
  const handleDuplicate = async () => {
    if (!id) return;
    const result = await store.DuplicatePipeline?.(id, {});
    const newId = getEntityId(result?.pipeline || result);
    if (newId) navigate(`/workflows/${newId}`);
  };
  const handleDelete = async () => { if (!id) return; await store.DeletePipeline?.(id); navigate('/workflows'); };

  if (loading && !item) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse space-y-4 w-full max-w-2xl">
          <div className="h-8 bg-muted rounded w-1/3" />
          <div className="h-4 bg-muted rounded w-1/2" />
          <div className="h-64 bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (loadError && !item) {
    return (
      <>
        <ErrorAlert error={loadError as Error} />
        <Link to="/workflows" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </>
    );
  }

  if (!item) {
    return (
      <Alert variant="warning" title={t('common.notFound')}>
        <Link to="/workflows" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </Alert>
    );
  }

  if (!accessLoading && !access.view) {
    return <Alert variant="warning" title={t('workflow.noViewAccess')}>
      <Link to="/workflows" className="text-primary hover:underline">{t('common.backToList')}</Link>
    </Alert>;
  }

  const status = item.status || 'PIPELINE_STATUS_DRAFT';
  const statusInfo = pipelineStatusMap[status] || pipelineStatusMap.PIPELINE_STATUS_DRAFT;
  const trigger = item.trigger_type || 'TRIGGER_TYPE_MANUAL';
  const triggerInfo = triggerTypeMap[trigger] || triggerTypeMap.TRIGGER_TYPE_MANUAL;

  return (
    <div className="space-y-6">
      {/* Breadcrumb + Header */}
      <div>
        <nav className="text-sm mb-3">
          <Link to="/workflows" className="text-muted-foreground hover:text-foreground">
            {t('workflow.pipelines')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{item.name || t('workflow.untitled')}</span>
        </nav>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-foreground">{item.name}</h1>
            <Badge variant={toBadgeVariant(statusInfo.variant)} size="sm">{t(statusInfo.labelKey)}</Badge>
          </div>
          <div className="flex items-center gap-2">
            {access.platformMember && status === 'PIPELINE_STATUS_ACTIVE' && (
              <Button size="sm" onClick={handleExecute}>
                <PlayIcon className="h-4 w-4 mr-1.5" />
                {t('workflow.run')}
              </Button>
            )}
            {access.owner && <Link to={`/workflows/${id}/edit`}>
              <Button variant="secondary" size="sm">
                <EditIcon className="h-4 w-4 mr-1.5" />
                {t('workflow.editor')}
              </Button>
            </Link>}
            {(access.owner || access.platformMember) && <div className="relative">
              <Button ref={actionsTriggerRef} variant="ghost" size="sm" onClick={() => setShowActions(!showActions)} aria-label={t('common.actions')}>
                <MoreIcon className="h-4 w-4" />
              </Button>
              <AnchoredMenu
                anchor={actionsTriggerRef}
                open={showActions}
                onClose={() => setShowActions(false)}
                align="end"
                ariaLabel={t('common.actions')}
                className="w-48 py-1"
              >
                {access.owner && status === 'PIPELINE_STATUS_DRAFT' && (
                  <button onClick={() => { handlePublish(); setShowActions(false); }} className="w-full text-left px-3 py-2 text-sm hover:bg-muted">
                    {t('workflow.publish')}
                  </button>
                )}
                {access.owner && status === 'PIPELINE_STATUS_ACTIVE' && (
                  <button onClick={() => { handlePause(); setShowActions(false); }} className="w-full text-left px-3 py-2 text-sm hover:bg-muted">
                    {t('workflow.pause')}
                  </button>
                )}
                {access.owner && status === 'PIPELINE_STATUS_PAUSED' && (
                  <button onClick={() => { handleResume(); setShowActions(false); }} className="w-full text-left px-3 py-2 text-sm hover:bg-muted">
                    {t('workflow.resume')}
                  </button>
                )}
                {access.platformMember && <button onClick={() => { handleDuplicate(); setShowActions(false); }} className="w-full text-left px-3 py-2 text-sm hover:bg-muted">
                  <CopyIcon className="h-3.5 w-3.5 inline mr-2" />
                  {t('workflow.duplicate')}
                </button>}
                {access.owner && <hr className="my-1 border-border" />}
                {access.owner && <button onClick={() => { setConfirmDelete(true); setShowActions(false); }} className="w-full text-left px-3 py-2 text-sm text-destructive hover:bg-muted">
                  {t('common.delete')}
                </button>}
              </AnchoredMenu>
            </div>}
          </div>
        </div>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-border overflow-x-auto" role="tablist">
        {visibleTabs.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={shownTab === tab.id}
            onClick={() => handleTabChange(tab.id)}
            className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors relative ${
              shownTab === tab.id ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t(tab.labelKey)}
            {shownTab === tab.id && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t" />}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {accessLoading && <div className="space-y-4" aria-busy="true" aria-label={t('workflow.loading')}>
        <div className="h-20 animate-pulse rounded-lg bg-muted" />
        <div className="h-48 animate-pulse rounded-lg bg-muted" />
      </div>}
      {!accessLoading && shownTab === 'overview' && <OverviewTab item={item} triggerInfo={triggerInfo} store={store} canView={access.view} canOperate={access.platformMember} onTabChange={handleTabChange} t={t} locale={locale} />}
      {!accessLoading && shownTab === 'executions' && <ExecutionsTab pipelineId={id!} store={store} onExecute={handleExecute} canRun={access.platformMember} t={t} locale={locale} />}
      {!accessLoading && shownTab === 'versions' && <VersionsTab pipelineId={id!} store={store} status={status} canEdit={access.owner} canOperate={access.platformMember} onChanged={reload} t={t} locale={locale} />}
      {!accessLoading && shownTab === 'schedules' && <SchedulesTab pipelineId={id!} store={store} canCreate={access.owner} canManage={access.platformOwner} t={t} locale={locale} />}
      {!accessLoading && shownTab === 'environments' && <EnvironmentsTab pipelineId={id!} store={store} canCreate={access.owner} canManage={access.platformOwner} t={t} />}
      {!accessLoading && shownTab === 'tests' && <TestsTab pipelineId={id!} store={store} canCreate={access.owner} canManage={access.platformOwner} canRun={access.platformMember} t={t} locale={locale} />}
      {!accessLoading && shownTab === 'settings' && <SettingsTab item={item} pipelineId={id!} store={store} onChanged={reload} t={t} />}

      <ConfirmDialog
        open={confirmDelete}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        title={t('workflow.deletePipeline')}
        description={t('workflow.deleteConfirm')}
        confirmLabel={t('common.delete')}
        destructive
      />
    </div>
  );
});

// ============================================================
// Overview
// ============================================================

function VersionPreview({ versionId, store, onClose, t, locale }: any) {
  const [version, setVersion] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    store.GetPipelineVersion?.(versionId)
      .then((result: any) => { if (!cancelled) setVersion(result?.version ?? result); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [versionId]);

  return (
    <Modal open onClose={onClose} title={t('workflow.versionInfo')} widthClass="max-w-xl"
      footer={<Button variant="secondary" onClick={onClose}>{t('common.close')}</Button>}>
      {loading ? <p className="text-sm text-muted-foreground">{t('workflow.loading')}</p>
        : error || !version ? <p className="text-sm text-destructive">{t('common.notFound')}</p>
          : <div className="space-y-4 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <strong className="text-lg text-foreground">v{version.version_number}</strong>
              {version.is_published && <Badge variant="success" size="sm">{t('workflow.published')}</Badge>}
              {version.is_draft && <Badge variant="secondary" size="sm">{t('workflow.draft')}</Badge>}
              {version.is_archived && <Badge variant="default" size="sm">{t('workflow.archived')}</Badge>}
            </div>
            {version.label && <p className="font-medium text-foreground">{version.label}</p>}
            {version.description && <p className="text-muted-foreground">{version.description}</p>}
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 border-t border-border pt-3">
              <dt className="text-muted-foreground">{t('workflow.nodes')}</dt><dd>{version.nodes?.length ?? 0}</dd>
              <dt className="text-muted-foreground">{t('workflow.versionDiff.connections')}</dt><dd>{version.connections?.length ?? 0}</dd>
              <dt className="text-muted-foreground">{t('common.createdAt')}</dt><dd>{formatDateTime(version.created_at, locale)}</dd>
            </dl>
            {(version.nodes || []).length > 0 && <div className="border-t border-border pt-3">
              <h4 className="font-medium text-foreground mb-2">{t('workflow.nodes')}</h4>
              <ul className="space-y-1">{version.nodes.map((node: any) =>
                <li key={getEntityId(node)} className="flex justify-between gap-4 rounded-md bg-muted/50 px-3 py-2">
                  <span className="truncate">{node.name || node.node_type_slug}</span>
                  <span className="text-xs text-muted-foreground font-mono">{node.node_type_slug}</span>
                </li>)}</ul>
            </div>}
          </div>}
    </Modal>
  );
}

function OverviewTab({ item, triggerInfo, store, canView, canOperate, onTabChange, t, locale }: any) {
  const [versions, setVersions] = useState<Row[]>([]);
  const [latestExecution, setLatestExecution] = useState<Row | null>(null);
  const [previewVersionId, setPreviewVersionId] = useState<string | null>(null);

  useEffect(() => {
    if (!canView) return;
    const pipelineId = getEntityId(item);
    store.ListPipelineVersions?.(pipelineId).then((res: any) => setVersions(unwrapItems(res))).catch(() => {});
    store.ListExecutions?.(pipelineId, { limit: 1 })
      .then((res: any) => setLatestExecution(unwrapItems(res)[0] ?? null)).catch(() => {});
  }, [getEntityId(item), canView]);

  const versionName = (versionId: string) => {
    const version = versions.find((entry) => getEntityId(entry) === versionId);
    return version ? `v${version.version_number}${version.label ? ` · ${version.label}` : ''}` : t('workflow.version');
  };
  const latestId = getEntityId(latestExecution);
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-px overflow-hidden rounded-lg border border-border bg-border">
        {canView ? <button type="button" onClick={() => onTabChange('executions')} className="bg-card p-4 text-left hover:bg-muted/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
          <span className="block text-xs text-muted-foreground">{t('workflow.totalExecutions')}</span>
          <span className="mt-1 block text-xl font-semibold tabular-nums text-foreground">{item.execution_count ?? 0}</span>
        </button> : <div className="bg-card p-4"><span className="block text-xs text-muted-foreground">{t('workflow.totalExecutions')}</span><span className="mt-1 block text-xl font-semibold tabular-nums text-foreground">{item.execution_count ?? 0}</span></div>}
        <div className="bg-card p-4">
          <span className="block text-xs text-muted-foreground">{t('workflow.lastExecution')}</span>
          {canView && canOperate && latestId ? <Link to={`/workflows/executions/${latestId}`} className="mt-1 block text-sm font-medium text-primary hover:underline underline-offset-2">
            {formatRelativeTime(item.last_executed_at || latestExecution?.created_at, locale)}
          </Link> : <span className="mt-1 block text-sm text-foreground">{item.last_executed_at ? formatRelativeTime(item.last_executed_at, locale) : t('workflow.neverRun')}</span>}
        </div>
        <div className="bg-card p-4">
          <span className="block text-xs text-muted-foreground">{t('workflow.timeout')}</span>
          <span className="mt-1 block text-sm font-medium text-foreground tabular-nums">{item.timeout_sec ? formatDuration(item.timeout_sec * 1000) : '—'}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(16rem,1fr)] gap-5 items-start">
        <Card>
          <CardHeader><CardTitle>{t('workflow.details')}</CardTitle></CardHeader>
          <CardContent>
            <dl className="divide-y divide-border">
              {item.description && (
                <div className="py-3 grid grid-cols-1 sm:grid-cols-[10rem_1fr] gap-1 sm:gap-4">
                  <dt className="text-sm font-medium text-muted-foreground">{t('workflow.field.description')}</dt>
                  <dd className="text-sm text-foreground whitespace-pre-wrap break-words">{item.description}</dd>
                </div>
              )}
              <div className="py-3 grid grid-cols-1 sm:grid-cols-[10rem_1fr] gap-1 sm:gap-4">
                <dt className="text-sm font-medium text-muted-foreground">{t('workflow.field.triggerType')}</dt>
                <dd className="text-sm text-foreground">{t(triggerInfo.labelKey)}</dd>
              </div>
              <div className="py-3 grid grid-cols-1 sm:grid-cols-[10rem_1fr] gap-1 sm:gap-4">
                <dt className="text-sm font-medium text-muted-foreground">{t('workflow.field.errorHandling')}</dt>
                <dd className="text-sm text-foreground">{t(errorHandlingOptions.find((option) => option.value === item.error_handling)?.labelKey || 'workflow.errorHandling.stop')}</dd>
              </div>
              {item.category && (
                <div className="py-3 grid grid-cols-1 sm:grid-cols-[10rem_1fr] gap-1 sm:gap-4">
                  <dt className="text-sm font-medium text-muted-foreground">{t('workflow.field.category')}</dt>
                  <dd className="text-sm text-foreground">{item.category}</dd>
                </div>
              )}
              <div className="py-3 grid grid-cols-1 sm:grid-cols-[10rem_1fr] gap-1 sm:gap-4">
                <dt className="text-sm font-medium text-muted-foreground">{t('common.createdAt')}</dt>
                <dd className="text-sm text-foreground">{formatDateTime(item.created_at, locale)}</dd>
              </div>
              <div className="py-3 grid grid-cols-1 sm:grid-cols-[10rem_1fr] gap-1 sm:gap-4">
                <dt className="text-sm font-medium text-muted-foreground">{t('common.updatedAt')}</dt>
                <dd className="text-sm text-foreground">{formatDateTime(item.updated_at, locale)}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>

      <div className="space-y-4">
        {item.tags?.length > 0 && (
          <Card>
            <CardHeader><CardTitle className="text-sm">{t('workflow.tags')}</CardTitle></CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-1.5">
                {item.tags.map((tag: string) => (<Badge key={tag} variant="secondary" size="sm">{tag}</Badge>))}
              </div>
            </CardContent>
          </Card>
        )}
        {item.owner && (
          <Card>
            <CardHeader><CardTitle className="text-sm">{t('workflow.owner')}</CardTitle></CardHeader>
            <CardContent><p className="text-sm text-foreground"><UserName id={getEntityId(item.owner)} showIdFallback={false} fallbackLabel={item.owner.display_name} linkToProfile /></p></CardContent>
          </Card>
        )}
        {canView && <Card>
          <CardHeader><CardTitle className="text-sm">{t('workflow.versionInfo')}</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-2">
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">{t('workflow.currentVersion')}</span>
              {item.current_version_id ? (canOperate ? <button type="button" onClick={() => setPreviewVersionId(item.current_version_id)} className="text-right text-primary hover:underline underline-offset-2">{versionName(item.current_version_id)}</button> : <span>{versionName(item.current_version_id)}</span>) : <span>—</span>}
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">{t('workflow.draftVersion')}</span>
              {item.draft_version_id ? (canOperate ? <button type="button" onClick={() => setPreviewVersionId(item.draft_version_id)} className="text-right text-primary hover:underline underline-offset-2">{versionName(item.draft_version_id)}</button> : <span>{versionName(item.draft_version_id)}</span>) : <span>—</span>}
            </div>
            <button type="button" onClick={() => onTabChange('versions')} className="text-xs text-primary hover:underline underline-offset-2">{t('workflow.versionHistory')} →</button>
          </CardContent>
        </Card>}
      </div>
      </div>
      {previewVersionId && <VersionPreview versionId={previewVersionId} store={store} onClose={() => setPreviewVersionId(null)} t={t} locale={locale} />}
    </div>
  );
}

// ============================================================
// Executions
// ============================================================

function ExecutionsTab({ pipelineId, store, onExecute, canRun, t, locale }: any) {
  const [executions, setExecutions] = useState<Row[]>([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    store.ListExecutions?.(pipelineId, { limit: 50 })
      .then((res: any) => setExecutions(unwrapItems(res)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [pipelineId]);

  useEffect(() => { load(); }, [load]);

  const filtered = statusFilter === 'all'
    ? executions
    : executions.filter((e) => (e.status || 'EXECUTION_STATUS_PENDING') === statusFilter);

  const handleCancel = async (execId: string) => {
    setBusyId(execId);
    try { await store.CancelExecution?.(execId, {}); load(); } finally { setBusyId(null); }
  };
  const handleRetry = async (execId: string) => {
    setBusyId(execId);
    try { await store.RetryExecution?.(execId, {}); load(); } finally { setBusyId(null); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div className="flex items-center gap-3">
          <h3 className="font-medium text-foreground">{t('workflow.executionHistory')}</h3>
          <Select value={statusFilter} onChange={(e: any) => setStatusFilter(e.target.value)} aria-label={t('workflow.filterStatus')} className="w-44">
            {executionStatusFilterOptions.map((o) => (<option key={o.value} value={o.value}>{t(o.labelKey)}</option>))}
          </Select>
        </div>
        {canRun && <Button size="sm" onClick={onExecute}>
          <PlayIcon className="h-3.5 w-3.5 mr-1.5" />
          {t('workflow.runNow')}
        </Button>}
      </div>
      <Card>
        <Table hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('workflow.execution')}</TableHead>
              <TableHead>{t('workflow.status.label')}</TableHead>
              <TableHead>{t('workflow.field.triggerType')}</TableHead>
              <TableHead>{t('workflow.duration')}</TableHead>
              <TableHead>{t('workflow.startedAt')}</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableEmpty colSpan={6} message={t('workflow.loading')} />
            ) : filtered.length === 0 ? (
              <TableEmpty colSpan={6} message={t('workflow.noExecutions')} />
            ) : (
              filtered.map((exec) => {
                const execId = getEntityId(exec);
                const execStatus = exec.status || 'EXECUTION_STATUS_PENDING';
                const info = executionStatusMap[execStatus] || executionStatusMap.EXECUTION_STATUS_PENDING;
                return (
                  <TableRow key={execId}>
                    <TableCell>
                      {canRun ? <Link to={`/workflows/executions/${execId}`} className="text-sm font-medium text-primary hover:underline underline-offset-2">
                        {formatDateTime(exec.created_at || exec.started_at, locale)}
                      </Link> : <span className="text-sm">{formatDateTime(exec.created_at || exec.started_at, locale)}</span>}
                    </TableCell>
                    <TableCell><Badge variant={toBadgeVariant(info.variant)} size="sm">{t(info.labelKey)}</Badge></TableCell>
                    <TableCell className="text-sm">{triggerTypeMap[exec.trigger_type]?.labelKey ? t(triggerTypeMap[exec.trigger_type].labelKey) : '—'}</TableCell>
                    <TableCell className="text-sm">{formatDuration(exec.duration_ms)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{formatDateTime(exec.started_at, locale)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {canRun && isExecutionActive(execStatus) && (
                          <Button variant="ghost" size="sm" disabled={busyId === execId} onClick={() => handleCancel(execId)}>{t('workflow.cancel')}</Button>
                        )}
                        {canRun && isExecutionRetryable(execStatus) && (
                          <Button variant="ghost" size="sm" disabled={busyId === execId} onClick={() => handleRetry(execId)}>{t('workflow.retry')}</Button>
                        )}
                        {canRun && <Link to={`/workflows/executions/${execId}`}>
                          <Button variant="ghost" size="sm">{t('common.view')}</Button>
                        </Link>}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// ============================================================
// Versions
// ============================================================

function VersionsTab({ pipelineId, store, status, canEdit, canOperate, onChanged, t, locale }: any) {
  const [versions, setVersions] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmRevert, setConfirmRevert] = useState<string | null>(null);
  const [previewVersionId, setPreviewVersionId] = useState<string | null>(null);

  // Compare two versions — DiffPipelineVersions.
  const [compareIds, setCompareIds] = useState<string[]>([]);
  const [diffOpen, setDiffOpen] = useState(false);
  const [diffLoading, setDiffLoading] = useState(false);
  const [diff, setDiff] = useState<any>(null);

  const load = useCallback(() => {
    setLoading(true);
    store.ListPipelineVersions?.(pipelineId)
      .then((res: any) => setVersions(unwrapItems(res)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [pipelineId]);

  useEffect(() => { load(); }, [load]);

  const handlePublish = async () => {
    setBusy(true);
    try { await store.PublishPipeline?.(pipelineId, {}); load(); onChanged?.(); } finally { setBusy(false); }
  };

  const handleRevert = async (versionId: string) => {
    setBusy(true);
    try { await store.RevertToVersion?.(versionId, {}); load(); onChanged?.(); } finally { setBusy(false); setConfirmRevert(null); }
  };

  const handleExport = async (versionId: string) => {
    const res = await store.ExportPipelineYAML?.(versionId);
    const yaml = res?.yaml_content || '';
    const name = res?.pipeline_name || 'pipeline';
    const blob = new Blob([yaml], { type: 'text/yaml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${name}.v${res?.version_number ?? ''}.yaml`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleCompare = (versionId: string) => {
    setCompareIds((prev) => {
      if (prev.includes(versionId)) return prev.filter((id) => id !== versionId);
      if (prev.length >= 2) return [prev[1], versionId];
      return [...prev, versionId];
    });
  };

  const runDiff = async () => {
    if (compareIds.length !== 2) return;
    setDiffOpen(true);
    setDiffLoading(true);
    try {
      const res = await store.DiffPipelineVersions?.(compareIds[0], compareIds[1]);
      setDiff(res);
    } finally {
      setDiffLoading(false);
    }
  };

  const versionLabel = (id: string) => {
    const v = versions.find((x) => getEntityId(x) === id);
    return v ? `v${v.version_number}` : id.slice(0, 8);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-between items-center gap-2">
        <h3 className="font-medium text-foreground">{t('workflow.versionHistory')}</h3>
        <div className="flex items-center gap-2">
          {canOperate && compareIds.length === 2 && (
            <Button size="sm" variant="secondary" onClick={runDiff}>{t('workflow.versionDiff.compare')}</Button>
          )}
          {canEdit && status === 'PIPELINE_STATUS_DRAFT' && (
            <Button size="sm" disabled={busy} onClick={handlePublish}>{t('workflow.publish')}</Button>
          )}
        </div>
      </div>
      {compareIds.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {t('workflow.versionDiff.hint')} {compareIds.length === 1 && `(${versionLabel(compareIds[0])})`}
        </p>
      )}
      <Card>
        <Table hoverable>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>{t('workflow.version')}</TableHead>
              <TableHead>{t('workflow.label')}</TableHead>
              <TableHead>{t('workflow.status.label')}</TableHead>
              <TableHead>{t('common.createdAt')}</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableEmpty colSpan={6} message={t('workflow.loading')} />
            ) : versions.length === 0 ? (
              <TableEmpty colSpan={6} message={t('workflow.noVersions')} />
            ) : (
              versions.map((v) => (
                <TableRow key={getEntityId(v)}>
                  <TableCell>
                    {canOperate && <input
                      type="checkbox"
                      aria-label={t('workflow.versionDiff.select')}
                      checked={compareIds.includes(getEntityId(v))}
                      onChange={() => toggleCompare(getEntityId(v))}
                      className="rounded border-input"
                    />}
                  </TableCell>
                  <TableCell className="text-sm">{canOperate ? <button type="button" className="font-medium text-primary hover:underline underline-offset-2" onClick={() => setPreviewVersionId(getEntityId(v))}>v{v.version_number}</button> : <span>v{v.version_number}</span>}</TableCell>
                  <TableCell className="text-sm">{v.label || '-'}</TableCell>
                  <TableCell>
                    {v.is_published && <Badge variant="success" size="sm">{t('workflow.published')}</Badge>}
                    {v.is_draft && <Badge variant="secondary" size="sm">{t('workflow.draft')}</Badge>}
                    {v.is_archived && <Badge variant="default" size="sm">{t('workflow.archived')}</Badge>}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{formatDateTime(v.created_at, locale)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {canOperate && <Button variant="ghost" size="sm" onClick={() => handleExport(getEntityId(v))} aria-label={t('workflow.exportYaml')}>
                        <DownloadIcon className="h-3.5 w-3.5" />
                      </Button>}
                      {canEdit && canOperate && !v.is_draft && (
                        <Button variant="ghost" size="sm" disabled={busy} onClick={() => setConfirmRevert(getEntityId(v))}>{t('workflow.revert')}</Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
      <ConfirmDialog
        open={!!confirmRevert}
        onCancel={() => setConfirmRevert(null)}
        onConfirm={() => confirmRevert && handleRevert(confirmRevert)}
        title={t('workflow.revertVersion')}
        description={t('workflow.revertConfirm')}
        confirmLabel={t('workflow.revert')}
      />
      {previewVersionId && <VersionPreview versionId={previewVersionId} store={store} onClose={() => setPreviewVersionId(null)} t={t} locale={locale} />}

      {/* Version diff — DiffPipelineVersions */}
      <Modal
        open={diffOpen}
        onClose={() => setDiffOpen(false)}
        title={compareIds.length === 2 ? `${t('workflow.versionDiff.compare')}: ${versionLabel(compareIds[0])} → ${versionLabel(compareIds[1])}` : t('workflow.versionDiff.compare')}
        widthClass="max-w-2xl"
        footer={<Button variant="secondary" onClick={() => setDiffOpen(false)}>{t('common.close')}</Button>}
      >
        {diffLoading ? (
          <div className="h-24 animate-pulse rounded-md bg-muted" aria-busy="true" />
        ) : diff ? (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="rounded-md border border-border p-2">
                <p className="text-lg font-bold text-foreground">{diff.nodes_added ?? 0}</p>
                <p className="text-xs text-muted-foreground">{t('workflow.versionDiff.nodesAdded')}</p>
              </div>
              <div className="rounded-md border border-border p-2">
                <p className="text-lg font-bold text-foreground">{diff.nodes_removed ?? 0}</p>
                <p className="text-xs text-muted-foreground">{t('workflow.versionDiff.nodesRemoved')}</p>
              </div>
              <div className="rounded-md border border-border p-2">
                <p className="text-lg font-bold text-foreground">{diff.nodes_modified ?? 0}</p>
                <p className="text-xs text-muted-foreground">{t('workflow.versionDiff.nodesModified')}</p>
              </div>
            </div>
            {(diff.node_diffs || []).length > 0 && (
              <div className="space-y-1">
                {diff.node_diffs.map((nd: any, idx: number) => (
                  <div key={idx} className="flex items-center gap-2 text-sm">
                    <Badge variant={nd.action === 'DIFF_ACTION_ADDED' ? 'success' : nd.action === 'DIFF_ACTION_REMOVED' ? 'error' : 'warning'} size="sm">
                      {t(nd.action === 'DIFF_ACTION_ADDED' ? 'workflow.versionDiff.added' : nd.action === 'DIFF_ACTION_REMOVED' ? 'workflow.versionDiff.removed' : 'workflow.versionDiff.modified')}
                    </Badge>
                    <span className="font-mono text-xs text-foreground">{nd.node_id}</span>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              {t('workflow.versionDiff.connections')}: +{diff.connections_added ?? 0} / -{diff.connections_removed ?? 0}
            </p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t('common.notFound')}</p>
        )}
      </Modal>
    </div>
  );
}

// ============================================================
// Schedules
// ============================================================

const emptySchedule = {
  name: '',
  schedule_type: 'SCHEDULE_TYPE_CRON',
  cron_expression: '',
  interval_seconds: 3600,
  timezone: 'UTC',
  is_active: true,
};

function SchedulesTab({ pipelineId, store, canCreate, canManage, t, locale }: any) {
  const [schedules, setSchedules] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState<null | { mode: 'create' | 'edit'; data: any }>(null);
  const [form, setForm] = useState<any>(emptySchedule);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    store.ListSchedules?.(pipelineId)
      .then((res: any) => setSchedules(unwrapItems(res)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [pipelineId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(emptySchedule); setErrors({}); setDialog({ mode: 'create', data: null }); };
  const openEdit = (s: any) => {
    setForm({
      name: s.name || '',
      schedule_type: s.schedule_type || 'SCHEDULE_TYPE_CRON',
      cron_expression: s.cron_expression || '',
      interval_seconds: s.interval_seconds || 3600,
      timezone: s.timezone || 'UTC',
      is_active: Boolean(s.is_active),
    });
    setErrors({});
    setDialog({ mode: 'edit', data: s });
  };

  const save = async () => {
    const errs = validateScheduleForm(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    try {
      if (dialog?.mode === 'edit' && dialog.data) {
        await store.UpdateSchedule?.(getEntityId(dialog.data), { schedule: { ...dialog.data, ...form } as any });
      } else {
        await store.CreateSchedule?.(pipelineId, {
          name: form.name.trim(),
          schedule_type: form.schedule_type,
          cron_expression: form.schedule_type === 'SCHEDULE_TYPE_CRON' ? form.cron_expression.trim() : undefined,
          interval_seconds: form.schedule_type === 'SCHEDULE_TYPE_INTERVAL' ? Number(form.interval_seconds) : undefined,
          timezone: form.timezone,
          is_active: form.is_active,
        });
      }
      setDialog(null);
      load();
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (s: any) => {
    const sid = getEntityId(s);
    if (s.is_active) await store.PauseSchedule?.(sid, {});
    else await store.ResumeSchedule?.(sid, {});
    load();
  };

  const remove = async (sid: string) => { await store.DeleteSchedule?.(sid); setConfirmId(null); load(); };

  const cronHint = form.schedule_type === 'SCHEDULE_TYPE_CRON' ? describeCron(form.cron_expression) : null;
  const intervalPreview = form.schedule_type === 'SCHEDULE_TYPE_INTERVAL' ? previewIntervalNextRun(Number(form.interval_seconds)) : null;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-medium text-foreground">{t('workflow.schedules')}</h3>
        {canCreate && <Button size="sm" variant="secondary" onClick={openCreate}>
          <PlusIcon className="h-3.5 w-3.5 mr-1.5" />
          {t('workflow.addSchedule')}
        </Button>}
      </div>
      <Card>
        <Table hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('workflow.scheduleName')}</TableHead>
              <TableHead>{t('workflow.scheduleType')}</TableHead>
              <TableHead>{t('workflow.expression')}</TableHead>
              <TableHead>{t('workflow.status.label')}</TableHead>
              <TableHead>{t('workflow.nextRun')}</TableHead>
              {canManage && <TableHead className="text-right">{t('common.actions')}</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableEmpty colSpan={canManage ? 6 : 5} message={t('workflow.loading')} />
            ) : schedules.length === 0 ? (
              <TableEmpty colSpan={canManage ? 6 : 5} message={t('workflow.noSchedules')} />
            ) : (
              schedules.map((s) => (
                <TableRow key={getEntityId(s)}>
                  <TableCell className="font-medium text-sm">{s.name}</TableCell>
                  <TableCell className="text-sm">
                    {s.schedule_type === 'SCHEDULE_TYPE_CRON' ? t('workflow.schedule.cron')
                      : s.schedule_type === 'SCHEDULE_TYPE_INTERVAL' ? t('workflow.schedule.interval')
                      : t('workflow.schedule.once')}
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {s.schedule_type === 'SCHEDULE_TYPE_INTERVAL' ? formatInterval(s.interval_seconds) : (s.cron_expression || '-')}
                  </TableCell>
                  <TableCell>
                    <Badge variant={s.is_active ? 'success' : 'secondary'} size="sm">
                      {s.is_active ? t('workflow.active') : t('workflow.paused')}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{formatDateTime(s.next_run_at, locale)}</TableCell>
                  {canManage && <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => toggle(s)}>{s.is_active ? t('workflow.pause') : t('workflow.resume')}</Button>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(s)} aria-label={t('common.edit')}><EditIcon className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => setConfirmId(getEntityId(s))} aria-label={t('common.delete')}>
                        <svg className="h-3.5 w-3.5 text-destructive" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
                      </Button>
                    </div>
                  </TableCell>}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <FormDialog
        open={!!dialog}
        onClose={() => setDialog(null)}
        onSubmit={save}
        submitLabel={t('common.save')}
        canSubmit={!!form.name.trim()}
        isSubmitting={saving}
        dirty={!!form.name.trim()}
        aria-label={dialog?.mode === 'edit' ? t('workflow.editSchedule') : t('workflow.addSchedule')}
      >
        <div className="space-y-3 px-3.5 pb-4 pt-2">
          <TitleInput
            value={form.name}
            onChange={(value) => setForm({ ...form, name: value })}
            placeholder={t('workflow.scheduleName')}
            initialFocus
            aria-label={t('workflow.scheduleName')}
          />
          {errors.name && <p className="text-sm text-destructive">{t(errors.name)}</p>}

          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 block">{t('workflow.scheduleType')}</label>
            <SegmentedControl
              aria-label={t('workflow.scheduleType')}
              value={form.schedule_type}
              onChange={(value) => setForm({ ...form, schedule_type: value })}
              options={scheduleTypeOptions.filter((o) => o.value !== 'SCHEDULE_TYPE_ONCE').map((o) => ({ value: o.value, label: t(o.labelKey) }))}
            />
          </div>

          {form.schedule_type === 'SCHEDULE_TYPE_INTERVAL' ? (
            <div>
              <Input label={t('workflow.intervalSeconds')} type="number" value={form.interval_seconds} onChange={(e) => setForm({ ...form, interval_seconds: e.target.value })} error={errors.interval_seconds ? t(errors.interval_seconds) : undefined} />
              {intervalPreview && <p className="mt-1 text-xs text-muted-foreground">{t('workflow.nextRun')}: {formatDateTime(intervalPreview, locale)}</p>}
            </div>
          ) : (
            <div>
              <Input label={t('workflow.cronExpression')} value={form.cron_expression} onChange={(e) => setForm({ ...form, cron_expression: e.target.value })} placeholder="0 9 * * *" className="font-mono" error={errors.cron_expression ? t(errors.cron_expression) : undefined} />
              {cronHint && <p className="mt-1 text-xs text-muted-foreground">{cronHint}</p>}
            </div>
          )}
          <Input label={t('workflow.timezone')} value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} />
        </div>
      </FormDialog>

      <ConfirmDialog
        open={!!confirmId}
        onCancel={() => setConfirmId(null)}
        onConfirm={() => confirmId && remove(confirmId)}
        title={t('workflow.deleteSchedule')}
        description={t('workflow.deleteScheduleConfirm')}
        confirmLabel={t('common.delete')}
        destructive
      />
    </div>
  );
}

// ============================================================
// Environments
// ============================================================

const emptyEnv = {
  name: '',
  description: '',
  environment_type: 'ENVIRONMENT_TYPE_DEVELOPMENT',
  variables: {} as Record<string, string>,
};

function EnvironmentsTab({ pipelineId, store, canCreate, canManage, t }: any) {
  const [environments, setEnvironments] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState<null | { mode: 'create' | 'edit'; data: any }>(null);
  const [form, setForm] = useState<any>(emptyEnv);
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    store.ListEnvironments?.(pipelineId)
      .then((res: any) => setEnvironments(unwrapItems(res)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [pipelineId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(emptyEnv); setDialog({ mode: 'create', data: null }); };
  const openEdit = (env: any) => {
    setForm({
      name: env.name || '',
      description: env.description || '',
      environment_type: env.environment_type || 'ENVIRONMENT_TYPE_DEVELOPMENT',
      variables: env.variables || {},
    });
    setDialog({ mode: 'edit', data: env });
  };

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (dialog?.mode === 'edit' && dialog.data) {
        await store.UpdateEnvironment?.(getEntityId(dialog.data), {
          environment: { ...dialog.data, name: form.name.trim(), description: form.description, environment_type: form.environment_type, variables: form.variables } as any,
        });
      } else {
        await store.CreateEnvironment?.(pipelineId, {
          name: form.name.trim(),
          description: form.description || undefined,
          environment_type: form.environment_type,
          credentials: [],
          variables: form.variables,
        });
      }
      setDialog(null);
      load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (envId: string) => { await store.DeleteEnvironment?.(envId); setConfirmId(null); load(); };

  const typeBadge: Record<string, string> = {
    ENVIRONMENT_TYPE_DEVELOPMENT: 'secondary',
    ENVIRONMENT_TYPE_STAGING: 'warning',
    ENVIRONMENT_TYPE_PRODUCTION: 'success',
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-medium text-foreground">{t('workflow.environments')}</h3>
        {canCreate && <Button size="sm" variant="secondary" onClick={openCreate}>
          <PlusIcon className="h-3.5 w-3.5 mr-1.5" />
          {t('workflow.addEnvironment')}
        </Button>}
      </div>
      {loading ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">{t('workflow.loading')}</CardContent></Card>
      ) : environments.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">{t('workflow.noEnvironments')}</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {environments.map((env) => (
            <Card key={getEntityId(env)}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-foreground">{env.name}</h4>
                  <Badge variant={(typeBadge[env.environment_type] as any) || 'secondary'} size="sm">
                    {t(environmentTypeOptions.find((option) => option.value === env.environment_type)?.labelKey || 'workflow.env.development')}
                  </Badge>
                </div>
                {env.description && <p className="text-sm text-muted-foreground mb-3">{env.description}</p>}
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{Object.keys(env.variables || {}).length} {t('workflow.variables')}</span>
                  {canManage && <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(env)}>{t('common.edit')}</Button>
                    <Button variant="ghost" size="sm" onClick={() => setConfirmId(getEntityId(env))}>{t('common.delete')}</Button>
                  </div>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <FormDialog
        open={!!dialog}
        onClose={() => setDialog(null)}
        onSubmit={save}
        submitLabel={t('common.save')}
        canSubmit={!!form.name.trim()}
        isSubmitting={saving}
        dirty={!!form.name.trim() || !!form.description.trim()}
        width="lg"
        aria-label={dialog?.mode === 'edit' ? t('workflow.editEnvironment') : t('workflow.addEnvironment')}
      >
        <div className="space-y-3 px-3.5 pb-4 pt-2">
          <TitleInput
            value={form.name}
            onChange={(value) => setForm({ ...form, name: value })}
            placeholder={t('workflow.environmentName')}
            initialFocus
            onEnter={() => document.getElementById('workflow-environment-description')?.focus()}
            aria-label={t('workflow.environmentName')}
          />
          <AutoGrowTextarea
            id="workflow-environment-description"
            value={form.description}
            onChange={(value) => setForm({ ...form, description: value })}
            placeholder={t('workflow.field.description')}
            minRows={1}
          />
          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 block">{t('workflow.environmentType')}</label>
            <SegmentedControl
              aria-label={t('workflow.environmentType')}
              value={form.environment_type}
              onChange={(value) => setForm({ ...form, environment_type: value })}
              options={environmentTypeOptions.map((o) => ({ value: o.value, label: t(o.labelKey) }))}
            />
          </div>
          <div>
            <p className="text-sm font-medium text-foreground mb-2">{t('workflow.variables')}</p>
            <KeyValueEditor
              value={form.variables}
              onChange={(next) => setForm({ ...form, variables: next })}
              keyLabel={t('workflow.varKey')}
              valueLabel={t('workflow.varValue')}
              addLabel={t('workflow.addVariable')}
              emptyLabel={t('workflow.noVariables')}
            />
          </div>
        </div>
      </FormDialog>

      <ConfirmDialog
        open={!!confirmId}
        onCancel={() => setConfirmId(null)}
        onConfirm={() => confirmId && remove(confirmId)}
        title={t('workflow.deleteEnvironment')}
        description={t('workflow.deleteEnvironmentConfirm')}
        confirmLabel={t('common.delete')}
        destructive
      />
    </div>
  );
}

// ============================================================
// Tests
// ============================================================

// The service has no ListTestSuites endpoint yet. A session-only suite list
// disappeared on reload, so this tab shows the persisted cases and results.
function TestsTab({ pipelineId, store, canCreate, canManage, canRun, t, locale }: any) {
  const [testCases, setTestCases] = useState<Row[]>([]);
  const [testResults, setTestResults] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  // Edit (UpdateTestCase) — assertions/mocks edited as raw JSON, the rest of
  // the tab only ever sends empty arrays for those on create.
  const [editCase, setEditCase] = useState<Row | null>(null);
  const [editForm, setEditForm] = useState({ name: '', description: '', assertions: '[]', node_mocks: '[]' });
  const [editError, setEditError] = useState<string | null>(null);
  const [editSaving, setEditSaving] = useState(false);

  // Result detail (GetTestResult) — list is wired, this opens one in full.
  const [resultId, setResultId] = useState<string | null>(null);
  const [resultDetail, setResultDetail] = useState<Row | null>(null);
  const [resultLoading, setResultLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      store.ListTestCases?.(pipelineId).then((res: any) => setTestCases(unwrapItems(res))).catch(() => {}),
      store.ListTestResults?.(pipelineId).then((res: any) => setTestResults(unwrapItems(res))).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [pipelineId]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await store.CreateTestCase?.(pipelineId, {
        name: form.name.trim(),
        description: form.description || undefined,
        node_mocks: [],
        assertions: [],
      });
      setDialog(false);
      setForm({ name: '', description: '' });
      load();
    } finally {
      setSaving(false);
    }
  };

  const generate = async () => { await store.GenerateTestCase?.(pipelineId, {}); load(); };

  const run = async (tcId: string) => {
    setBusyId(tcId);
    try { await store.RunTestCase?.(tcId, {}); load(); } finally { setBusyId(null); }
  };

  const remove = async (tcId: string) => { await store.DeleteTestCase?.(tcId); setConfirmId(null); load(); };

  const openEditCase = (tc: Row) => {
    setEditCase(tc);
    setEditError(null);
    setEditForm({
      name: tc.name || '',
      description: tc.description || '',
      assertions: JSON.stringify(tc.assertions || [], null, 2),
      node_mocks: JSON.stringify(tc.node_mocks || [], null, 2),
    });
  };

  const saveEditCase = async () => {
    if (!editCase) return;
    let assertions: any[]; let node_mocks: any[];
    try {
      assertions = JSON.parse(editForm.assertions || '[]');
      node_mocks = JSON.parse(editForm.node_mocks || '[]');
    } catch {
      setEditError(t('workflow.invalidJson'));
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      await store.UpdateTestCase?.(getEntityId(editCase), {
        test_case: { ...editCase, name: editForm.name.trim(), description: editForm.description, assertions, node_mocks },
      });
      setEditCase(null);
      load();
    } catch (e: any) {
      setEditError(e?.message || t('workflow.saveError'));
    } finally {
      setEditSaving(false);
    }
  };

  const openResult = async (trId: string) => {
    setResultId(trId);
    setResultLoading(true);
    setResultDetail(null);
    try {
      const res = await store.GetTestResult?.(trId);
      setResultDetail((res as any)?.result ?? res ?? null);
    } finally {
      setResultLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="font-medium text-foreground">{t('workflow.testCases')}</h3>
          {canCreate && <div className="flex gap-2">
            <Button size="sm" variant="secondary" onClick={generate}>{t('workflow.generateTest')}</Button>
            <Button size="sm" variant="secondary" onClick={() => setDialog(true)}>
              <PlusIcon className="h-3.5 w-3.5 mr-1.5" />
              {t('workflow.createTest')}
            </Button>
          </div>}
        </div>
        <Card>
          <Table hoverable>
            <TableHeader>
              <TableRow>
                <TableHead>{t('workflow.testName')}</TableHead>
                <TableHead>{t('workflow.assertions')}</TableHead>
                <TableHead>{t('workflow.mocks')}</TableHead>
                <TableHead>{t('common.createdAt')}</TableHead>
                <TableHead className="text-right">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableEmpty colSpan={5} message={t('workflow.loading')} />
              ) : testCases.length === 0 ? (
                <TableEmpty colSpan={5} message={t('workflow.noTestCases')} />
              ) : (
                testCases.map((tc) => (
                  <TableRow key={getEntityId(tc)}>
                    <TableCell className="font-medium text-sm">{tc.name}</TableCell>
                    <TableCell className="text-sm">{(tc.assertions || []).length}</TableCell>
                    <TableCell className="text-sm">{(tc.node_mocks || []).length}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{formatDateTime(tc.created_at, locale)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {canRun && <Button variant="ghost" size="sm" disabled={busyId === getEntityId(tc)} onClick={() => run(getEntityId(tc))}>{t('workflow.runTest')}</Button>}
                        {canManage && <Button variant="ghost" size="sm" onClick={() => openEditCase(tc)}>{t('common.edit')}</Button>}
                        {canManage && <Button variant="ghost" size="sm" onClick={() => setConfirmId(getEntityId(tc))}>{t('common.delete')}</Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </div>

      {testResults.length > 0 && (
        <div className="space-y-4">
          <h3 className="font-medium text-foreground">{t('workflow.recentResults')}</h3>
          <Card>
            <Table hoverable>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('workflow.result')}</TableHead>
                  <TableHead>{t('workflow.status.label')}</TableHead>
                  <TableHead>{t('workflow.coverage')}</TableHead>
                  <TableHead>{t('workflow.duration')}</TableHead>
                  <TableHead>{t('common.createdAt')}</TableHead>
                  <TableHead className="text-right">{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {testResults.map((tr) => {
                  const coverage = tr.branches_total > 0 ? Math.round((tr.branches_covered / tr.branches_total) * 100) : 0;
                  return (
                    <TableRow key={getEntityId(tr)}>
                      <TableCell className="font-mono text-sm">{getEntityId(tr).slice(0, 8)}</TableCell>
                      <TableCell>
                        <Badge variant={tr.status === 'TEST_STATUS_PASSED' ? 'success' : tr.status === 'TEST_STATUS_FAILED' ? 'error' : 'secondary'} size="sm">
                          {tr.status === 'TEST_STATUS_PASSED' ? t('workflow.passed') : tr.status === 'TEST_STATUS_FAILED' ? t('workflow.failed') : t('workflow.loading')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div className="h-full bg-primary rounded-full" style={{ width: `${coverage}%` }} />
                          </div>
                          <span className="text-xs text-muted-foreground">{coverage}%</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{formatDuration(tr.duration_ms)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{formatDateTime(tr.created_at, locale)}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => openResult(getEntityId(tr))}>{t('common.view')}</Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </div>
      )}

      <Modal
        open={dialog}
        onClose={() => setDialog(false)}
        title={t('workflow.createTest')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={create} isLoading={saving} disabled={!form.name.trim()}>{t('common.save')}</Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input label={t('workflow.testName')} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Textarea label={t('workflow.field.description')} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
          <p className="text-xs text-muted-foreground">{t('workflow.testCaseHint')}</p>
        </div>
      </Modal>

      {/* Edit test case — UpdateTestCase */}
      <Modal
        open={!!editCase}
        onClose={() => setEditCase(null)}
        title={t('workflow.testSuites.editTest')}
        widthClass="max-w-2xl"
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditCase(null)}>{t('common.cancel')}</Button>
            <Button onClick={saveEditCase} isLoading={editSaving} disabled={!editForm.name.trim()}>{t('common.save')}</Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input label={t('workflow.testName')} value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
          <Textarea label={t('workflow.field.description')} value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} rows={2} />
          <Textarea label={t('workflow.assertions')} value={editForm.assertions} onChange={(e) => setEditForm({ ...editForm, assertions: e.target.value })} rows={5} className="font-mono text-xs" />
          <Textarea label={t('workflow.mocks')} value={editForm.node_mocks} onChange={(e) => setEditForm({ ...editForm, node_mocks: e.target.value })} rows={5} className="font-mono text-xs" />
          {editError && <Alert variant="error">{editError}</Alert>}
        </div>
      </Modal>

      {/* Test result detail — GetTestResult */}
      <Modal
        open={!!resultId}
        onClose={() => { setResultId(null); setResultDetail(null); }}
        title={t('workflow.testSuites.resultDetail')}
        widthClass="max-w-2xl"
        footer={<Button variant="secondary" onClick={() => setResultId(null)}>{t('common.close')}</Button>}
      >
        {resultLoading ? (
          <div className="h-24 animate-pulse rounded-md bg-muted" aria-busy="true" />
        ) : resultDetail ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-muted-foreground">{t('workflow.status.label')}: </span>{resultDetail.status === 'TEST_STATUS_PASSED' ? t('workflow.passed') : resultDetail.status === 'TEST_STATUS_FAILED' ? t('workflow.failed') : t('workflow.loading')}</div>
              <div><span className="text-muted-foreground">{t('workflow.duration')}: </span>{formatDuration(resultDetail.duration_ms)}</div>
              <div><span className="text-muted-foreground">{t('workflow.testSuites.nodesExecuted')}: </span>{resultDetail.nodes_executed}/{resultDetail.nodes_total}</div>
              <div><span className="text-muted-foreground">{t('workflow.coverage')}: </span>{resultDetail.branches_total > 0 ? Math.round((resultDetail.branches_covered / resultDetail.branches_total) * 100) : 0}%</div>
            </div>
            {(resultDetail.assertion_results || []).length > 0 && (
              <div>
                <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-1.5">{t('workflow.assertions')}</h4>
                <div className="space-y-1">
                  {resultDetail.assertion_results.map((ar: any, idx: number) => (
                    <div key={idx} className="flex items-start gap-2 text-sm">
                      <Badge variant={ar.passed ? 'success' : 'error'} size="sm">{ar.passed ? t('workflow.passed') : t('workflow.failed')}</Badge>
                      <div className="min-w-0">
                        <p className="font-mono text-xs text-foreground truncate">{ar.expression}</p>
                        {ar.description && <p className="text-xs text-muted-foreground">{ar.description}</p>}
                        {!ar.passed && ar.error && <p className="text-xs text-destructive">{ar.error}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(resultDetail.node_results || []).length > 0 && (
              <div>
                <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-1.5">{t('workflow.testSuites.nodeResults')}</h4>
                <div className="space-y-1">
                  {resultDetail.node_results.map((nr: any, idx: number) => (
                    <div key={idx} className="flex items-center gap-2 text-xs">
                      <Badge variant={toBadgeVariant((executionStatusMap[nr.status]?.variant) || 'secondary')} size="sm">{t(executionStatusMap[nr.status]?.labelKey || 'workflow.exec.pending')}</Badge>
                      <span className="font-mono text-foreground">{nr.node_id}</span>
                      {nr.was_mocked && <span className="text-muted-foreground">({t('workflow.testSuites.mocked')})</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t('common.notFound')}</p>
        )}
      </Modal>

      <ConfirmDialog
        open={!!confirmId}
        onCancel={() => setConfirmId(null)}
        onConfirm={() => confirmId && remove(confirmId)}
        title={t('workflow.deleteTest')}
        description={t('workflow.deleteTestConfirm')}
        confirmLabel={t('common.delete')}
        destructive
      />
    </div>
  );
}

// ============================================================
// Settings (per-pipeline governance)
// ============================================================

function SettingsTab({ item, pipelineId, store, onChanged, t }: any) {
  const auth = useAuth();
  const policyStore = useWorkflowPipelinePolicy();
  const [form, setForm] = useState({
    name: item.name || '',
    description: item.description || '',
    error_handling: item.error_handling || 'ERROR_HANDLING_STOP_ON_ERROR',
    trigger_type: item.trigger_type || 'TRIGGER_TYPE_MANUAL',
    timeout_sec: item.timeout_sec || 0,
    visibility: item.metadata?.visibility === 'platform' ? 'platform' : 'private',
  });
  const [runAsType, setRunAsType] = useState<'profile' | 'team' | 'role'>('profile');
  const [runAsId, setRunAsId] = useState('');
  const [runAsLabel, setRunAsLabel] = useState('');
  const [runAsPickerOpen, setRunAsPickerOpen] = useState(false);
  const [ownProfiles, setOwnProfiles] = useState<Array<{ id: string; name: string }>>([]);
  const [profilesLoading, setProfilesLoading] = useState(true);
  const [runAsBusy, setRunAsBusy] = useState(false);
  const [runAsError, setRunAsError] = useState('');
  const [runAsMessage, setRunAsMessage] = useState('');
  const [runAsConfirm, setRunAsConfirm] = useState(false);
  const [runAsRequests, setRunAsRequests] = useState<PipelineRunAsRequest[]>([]);
  const [decidableRunAsRequests, setDecidableRunAsRequests] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [policy, setPolicy] = useState<any>(null);
  const [policyLoaded, setPolicyLoaded] = useState(false);
  // Resolved (inherited) policy — ResolvePipelineContext walks the scope
  // chain (tenant → product → platform) rather than reading one direct
  // record, so it can differ from the policy above when none is attached
  // directly to this pipeline.
  const [resolved, setResolved] = useState<any>(null);
  const [resolvedLoaded, setResolvedLoaded] = useState(false);

  useEffect(() => {
    if (!auth.userId) return;
    let cancelled = false;
    userprofileApi.ListUserprofilesForService(auth.userId, { pagination: { limit: 100, cursor: '', direction: 'forward' } })
      .then((response) => {
        if (cancelled) return;
        const profiles = (response.userprofiles?.profiles || [])
          .filter((profile) => profile.entity?.id && profile.user?.id === auth.userId && profile.status !== 'PROFILE_STATUS_INACTIVE')
          .map((profile) => ({ id: profile.entity!.id, name: profile.display_name || profile.username || profile.entity!.id }));
        setOwnProfiles(profiles);
        setRunAsId((current) => current || profiles.find((profile) => profile.id === auth.activeProfileId)?.id || profiles[0]?.id || '');
      })
      .catch(() => { if (!cancelled) setOwnProfiles([]); })
      .finally(() => { if (!cancelled) setProfilesLoading(false); });
    return () => { cancelled = true; };
  }, [auth.userId, auth.activeProfileId]);

  const refreshRunAsRequests = useCallback(async () => {
    try {
      const requests = await listAllRunAsRequests({ pipeline: { id: pipelineId } as Entity });
      setRunAsRequests(requests);
      const permissions = await Promise.all(requests.map(async (request) => [request.entity?.id, await canDecideRunAsRequest(auth.activeProfileId || '', request)] as const));
      setDecidableRunAsRequests(new Set(permissions.filter(([, allowed]) => allowed).map(([id]) => id).filter((id): id is string => !!id)));
    } catch {
      setRunAsRequests([]);
    }
  }, [pipelineId, auth.activeProfileId]);

  useEffect(() => { void refreshRunAsRequests(); }, [refreshRunAsRequests]);

  useEffect(() => {
    const ent = item.entity;
    if (!ent?.id) { setPolicyLoaded(true); setResolvedLoaded(true); return; }
    policyStore.GetPipelinePolicyByEntity?.(ent.type_name || 'pipeline', ent.id)
      .then((p: any) => setPolicy(p))
      .catch(() => setPolicy(null))
      .finally(() => setPolicyLoaded(true));
    policyStore.ResolvePipelineContext?.({ scope_entity: ent, scope_chain: [ent] })
      .then((r: any) => setResolved(r))
      .catch(() => setResolved(null))
      .finally(() => setResolvedLoaded(true));
  }, [pipelineId]);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const pipeline: any = {
        ...item,
        name: form.name.trim(),
        description: form.description,
        error_handling: form.error_handling,
        trigger_type: form.trigger_type,
        timeout_sec: Number(form.timeout_sec) || 0,
        metadata: { ...item.metadata, visibility: form.visibility },
      };
      await store.UpdatePipeline?.(pipelineId, { pipeline });
      setSaved(true);
      onChanged?.();
      setTimeout(() => setSaved(false), 4000);
    } finally {
      setSaving(false);
    }
  };

  const entityRef = (id: string, typeName = ''): Entity => ({ id, type_name: typeName, type_id: typeName, platform_id: '', tenant_id: '', product_id: '' });
  const requestedActor = () => ({ entity: entityRef(runAsId.trim(), runAsType), display_name: '', avatar: '' });

  const beginRunAsChange = async () => {
    if (!runAsId.trim()) return;
    setRunAsBusy(true);
    setRunAsError('');
    try {
      const result = await workflowApi.GetPipelineRunAsEligibility(pipelineId, {
        pipeline: entityRef(pipelineId, 'pipeline'), actor: requestedActor(),
      });
      if (result.eligibility === 'PIPELINE_RUN_AS_ELIGIBILITY_UNAVAILABLE') {
        setRunAsError(result.reason || 'Этот актор недоступен для запуска пайплайна.');
        return;
      }
      setRunAsConfirm(true);
    } catch (error) {
      setRunAsError(error instanceof Error ? error.message : 'Не удалось проверить права на актора.');
    } finally {
      setRunAsBusy(false);
    }
  };

  const submitRunAsChange = async () => {
    setRunAsBusy(true);
    setRunAsError('');
    try {
      await workflowApi.SetPipelineRunAs(pipelineId, {
        pipeline: entityRef(pipelineId, 'pipeline'), requested_actor: requestedActor(),
        request_message: runAsMessage.trim(),
      });
      setRunAsConfirm(false);
      setRunAsMessage('');
      await refreshRunAsRequests();
      onChanged?.();
    } catch (error) {
      setRunAsError(error instanceof Error ? error.message : 'Не удалось отправить запрос.');
    } finally {
      setRunAsBusy(false);
    }
  };

  const actOnRunAsRequest = async (request: PipelineRunAsRequest, action: 'approve' | 'reject' | 'cancel' | 'revoke') => {
    const id = request.entity?.id;
    if (!id) return;
    setRunAsBusy(true);
    setRunAsError('');
    try {
      if (action === 'cancel') await workflowApi.CancelPipelineRunAsRequest(id, { request: entityRef(id, 'pipeline_run_as_request') });
      else if (action === 'revoke') await workflowApi.RevokePipelineRunAsAuthorization(id, { request: entityRef(id, 'pipeline_run_as_request') });
      else await workflowApi.DecidePipelineRunAsRequest(id, {
        request: entityRef(id, 'pipeline_run_as_request'), decision: action === 'approve' ? 'PIPELINE_RUN_AS_DECISION_APPROVE' : 'PIPELINE_RUN_AS_DECISION_REJECT',
      });
      await refreshRunAsRequests();
      onChanged?.();
    } catch (error) {
      setRunAsError(error instanceof Error ? error.message : 'Не удалось изменить запрос.');
    } finally {
      setRunAsBusy(false);
    }
  };

  const p = policy?.policy || policy;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <Card>
          <CardHeader><CardTitle>{t('workflow.generalSettings')}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <Input label={t('workflow.field.name')} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Textarea label={t('workflow.field.description')} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Select label={t('workflow.field.triggerType')} value={form.trigger_type} onChange={(e: any) => setForm({ ...form, trigger_type: e.target.value })}>
                {triggerTypeOptions.map((o) => (<option key={o.value} value={o.value}>{t(o.labelKey)}</option>))}
              </Select>
              <Select label={t('workflow.field.errorHandling')} value={form.error_handling} onChange={(e: any) => setForm({ ...form, error_handling: e.target.value })}>
                {errorHandlingOptions.map((o) => (<option key={o.value} value={o.value}>{t(o.labelKey)}</option>))}
              </Select>
            </div>
            <Input label={t('workflow.timeout')} type="number" value={form.timeout_sec} onChange={(e) => setForm({ ...form, timeout_sec: e.target.value })} />
            <Select label={t('workflow.visibility.label')} value={form.visibility} onChange={(e: any) => setForm({ ...form, visibility: e.target.value })}>
              <option value="private">{t('workflow.visibility.private')}</option>
              <option value="platform">{t('workflow.visibility.platform')}</option>
            </Select>
            <p className="text-xs text-muted-foreground">{t('workflow.visibility.hint')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>{t('workflow.runAs')}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">Сейчас запускается от имени: {item.run_as?.display_name || item.run_as?.entity?.id || 'не задано'}.</p>
            {item.pending_run_as_request && <Alert variant="warning">Запрос на смену актора ожидает подтверждения.</Alert>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Select label="Тип актора" value={runAsType} onChange={(e: any) => {
                const type = e.target.value as 'profile' | 'team' | 'role';
                setRunAsType(type);
                setRunAsId(type === 'profile' ? (ownProfiles.find((profile) => profile.id === auth.activeProfileId)?.id || ownProfiles[0]?.id || '') : '');
                setRunAsLabel('');
                setRunAsError('');
                setRunAsPickerOpen(type !== 'profile');
              }}>
                <option value="profile">Профиль</option><option value="team">Команда</option><option value="role">Роль</option>
              </Select>
              {runAsType === 'profile' ? <Input label="ID профиля" value={runAsId} onChange={(e) => setRunAsId(e.target.value)} /> : (
                <div>
                  <span className="mb-1.5 block text-sm font-medium text-foreground">{runAsType === 'team' ? 'Команда' : 'Роль'}</span>
                  <button type="button" onClick={() => setRunAsPickerOpen(true)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-left text-sm text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                    {runAsLabel || (runAsType === 'team' ? 'Найти команду' : 'Найти команду и роль')}
                  </button>
                </div>
              )}
            </div>
            {runAsType === 'profile' && <Select label="Мои профили" value={ownProfiles.some((profile) => profile.id === runAsId) ? runAsId : ''} onChange={(e: any) => setRunAsId(e.target.value)} disabled={profilesLoading}>
              <option value="">Выберите профиль или введите ID выше</option>
              {ownProfiles.map((profile) => <option key={profile.id} value={profile.id}>{profile.name}</option>)}
            </Select>}
            <Button onClick={beginRunAsChange} isLoading={runAsBusy} disabled={!runAsId.trim() || runAsBusy}>Изменить актора запуска</Button>
            {runAsError && <Alert variant="error">{runAsError}</Alert>}
            {runAsRequests.length > 0 && <div className="space-y-2 border-t border-border pt-3">
              <h3 className="text-sm font-medium">Запросы на запуск от имени</h3>
              {runAsRequests.map((request) => <div key={request.entity?.id} className="rounded border border-border p-3 text-sm space-y-2">
                <div>{request.requested_actor?.display_name || request.requested_actor?.entity?.id} · {request.status?.replace('PIPELINE_RUN_AS_REQUEST_STATUS_', '')}</div>
                {request.status === 'PIPELINE_RUN_AS_REQUEST_STATUS_PENDING' && <div className="flex gap-2">
                  {request.entity?.id && decidableRunAsRequests.has(request.entity.id) && <><Button size="sm" onClick={() => actOnRunAsRequest(request, 'approve')} disabled={runAsBusy}>Подтвердить</Button>
                  <Button size="sm" variant="secondary" onClick={() => actOnRunAsRequest(request, 'reject')} disabled={runAsBusy}>Отклонить</Button></>}
                  {request.requested_by?.entity?.id === auth.activeProfileId && <Button size="sm" variant="secondary" onClick={() => actOnRunAsRequest(request, 'cancel')} disabled={runAsBusy}>Отменить</Button>}
                </div>}
                {request.status === 'PIPELINE_RUN_AS_REQUEST_STATUS_APPROVED' && request.entity?.id && decidableRunAsRequests.has(request.entity.id) && <Button size="sm" variant="secondary" onClick={() => actOnRunAsRequest(request, 'revoke')} disabled={runAsBusy}>Отозвать разрешение</Button>}
              </div>)}
            </div>}
          </CardContent>
        </Card>

        {runAsType !== 'profile' && <RunAsActorPicker
          open={runAsPickerOpen}
          kind={runAsType}
          onClose={() => setRunAsPickerOpen(false)}
          onSelect={(id, label) => { setRunAsId(id); setRunAsLabel(label); setRunAsError(''); }}
        />}

        <Modal open={runAsConfirm} onClose={() => setRunAsConfirm(false)} title="Смена актора запуска"
          description="Если у вас нет права использовать этого актора, ему или уполномоченному участнику придёт запрос на подтверждение. До подтверждения пайплайн продолжит работать с прежней идентичностью."
          footer={<><Button variant="secondary" onClick={() => setRunAsConfirm(false)}>Отмена</Button><Button onClick={submitRunAsChange} isLoading={runAsBusy}>Продолжить</Button></>}>
          <Textarea label="Сообщение для согласующего" value={runAsMessage} onChange={(e) => setRunAsMessage(e.target.value)} rows={3} />
          {runAsError && <Alert variant="error">{runAsError}</Alert>}
        </Modal>

        {saved && <Alert variant="success">{t('workflow.saveSuccess')}</Alert>}
        <div className="flex justify-end">
          <Button onClick={save} isLoading={saving}>{t('common.save')}</Button>
        </div>
      </div>

      <div>
        <Card>
          <CardHeader><CardTitle className="text-sm">{t('workflow.governingPolicy')}</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-3">
            {!policyLoaded ? (
              <p className="text-muted-foreground">{t('workflow.loading')}</p>
            ) : !p?.name ? (
              <p className="text-muted-foreground">{t('workflow.noPolicy')}</p>
            ) : (
              <dl className="space-y-2">
                <div className="flex justify-between"><dt className="text-muted-foreground">{t('workflow.field.name')}</dt><dd className="text-foreground">{p.name}</dd></div>
                {p.require_publish_approval && (
                  <div className="flex justify-between"><dt className="text-muted-foreground">{t('workflow.requiresApproval')}</dt><dd><Badge variant="warning" size="sm">{t('workflow.yes')}</Badge></dd></div>
                )}
                {p.max_concurrent_executions ? (
                  <div className="flex justify-between"><dt className="text-muted-foreground">{t('workflow.maxConcurrent')}</dt><dd className="text-foreground">{p.max_concurrent_executions}</dd></div>
                ) : null}
                {p.default_timeout_sec ? (
                  <div className="flex justify-between"><dt className="text-muted-foreground">{t('workflow.defaultTimeout')}</dt><dd className="text-foreground">{p.default_timeout_sec}s</dd></div>
                ) : null}
                {(p.allowed_trigger_types || []).length > 0 && (
                  <div>
                    <dt className="text-muted-foreground mb-1">{t('workflow.allowedTriggers')}</dt>
                    <dd className="flex flex-wrap gap-1">{p.allowed_trigger_types.map((tt: string) => (<Badge key={tt} variant="secondary" size="sm">{t(triggerTypeMap[tt]?.labelKey || 'workflow.trigger.manual')}</Badge>))}</dd>
                  </div>
                )}
              </dl>
            )}
            <p className="text-xs text-muted-foreground pt-2 border-t border-border">{t('workflow.policyReadOnlyHint')}</p>
          </CardContent>
        </Card>

        {/* Resolved (inherited) policy — ResolvePipelineContext */}
        <Card className="mt-4">
          <CardHeader><CardTitle className="text-sm">{t('workflow.resolvedPolicy.title')}</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-3">
            {!resolvedLoaded ? (
              <p className="text-muted-foreground">{t('workflow.loading')}</p>
            ) : !resolved?.effective_policy?.name ? (
              <p className="text-muted-foreground">{t('workflow.noPolicy')}</p>
            ) : (
              <dl className="space-y-2">
                <div className="flex justify-between"><dt className="text-muted-foreground">{t('workflow.field.name')}</dt><dd className="text-foreground">{resolved.effective_policy.name}</dd></div>
                {(resolved.policy_chain || []).length > 1 && (
                  <div className="flex justify-between"><dt className="text-muted-foreground">{t('workflow.resolvedPolicy.chainDepth')}</dt><dd className="text-foreground">{resolved.policy_chain.length}</dd></div>
                )}
              </dl>
            )}
            {(resolved?.allowed_node_types || []).length > 0 && (
              <p className="text-xs text-muted-foreground pt-2 border-t border-border">
                {t('workflow.resolvedPolicy.allowedNodeTypes', { count: resolved.allowed_node_types.length })}
              </p>
            )}
            <p className="text-xs text-muted-foreground pt-2 border-t border-border">{t('workflow.resolvedPolicy.hint')}</p>
          </CardContent>
        </Card>

        {form.trigger_type === 'TRIGGER_TYPE_WEBHOOK' && <WebhookCard item={item} store={store} t={t} />}
      </div>
    </div>
  );
}

function WebhookCard({ item, store, t }: any) {
  const token = item.slug || getEntityId(item);
  const url = `${window.location.origin}/api/workflow/workflows/webhooks/${token}`;
  const [payload, setPayload] = useState('{}');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const send = async () => {
    setSending(true);
    setResult(null);
    try {
      const res = await store.TriggerWebhook?.(token, { webhook_token: token, signature: '', payload });
      setResult(res?.accepted ? t('workflow.webhookAccepted') : (res?.message || t('workflow.webhookRejected')));
    } catch {
      setResult(t('workflow.webhookRejected'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="mt-4">
      <CardHeader><CardTitle className="text-sm">{t('workflow.webhookTrigger')}</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div>
          <p className="text-xs text-muted-foreground mb-1">{t('workflow.webhookUrl')}</p>
          <code className="block text-xs bg-muted rounded px-2 py-1.5 break-all text-foreground">{url}</code>
        </div>
        <Textarea label={t('workflow.testPayload')} value={payload} onChange={(e) => setPayload(e.target.value)} rows={3} className="font-mono text-xs" />
        <Button size="sm" variant="secondary" onClick={send} isLoading={sending}>{t('workflow.sendTestEvent')}</Button>
        {result && <p className="text-xs text-muted-foreground">{result}</p>}
      </CardContent>
    </Card>
  );
}

export default WorkflowDetailPage;
